import React, { useState, useEffect, useCallback } from 'react';
import {
  getLists,
  createList,
  updateList,
  deleteList,
  getListContacts,
  createListContact,
  updateListContact,
  deleteListContact,
  getCampaigns,
  getCampaignMetrics,
  createCampaign,
  updateCampaign,
  deleteCampaign,
  getMailupStatus,
  getMailupOAuthUrl,
  mailupSyncLists,
  mailupSyncAll,
  mailupSyncListContacts,
} from '../services/database';
import { ClipboardIcon, RefreshIcon, XIcon, SettingsIcon } from './icons/AdminIcons';
import Modal from './Modal';
import DatabaseDataSection from './DatabaseDataSection';
import './DatabaseSection.css';

type MainTab = 'database' | 'mailup';
type SubTab = 'lists' | 'contacts' | 'campaigns' | 'metrics' | 'mailup';

interface DatabaseSectionProps {
  isDarkMode?: boolean;
}

const DatabaseSection: React.FC<DatabaseSectionProps> = ({ isDarkMode }) => {
  const [mainTab, setMainTab] = useState<MainTab>('database');
  const [subTab, setSubTab] = useState<SubTab>('lists');
  const [lists, setLists] = useState<any[]>([]);
  const [contacts, setContacts] = useState<any[]>([]);
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [selectedListId, setSelectedListId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [mailupConnected, setMailupConnected] = useState(false);
  const [mailupSyncing, setMailupSyncing] = useState(false);
  const [modal, setModal] = useState<'list' | 'contact' | 'campaign' | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formList, setFormList] = useState({ name: '', description: '', external_id: '' });
  const [formContact, setFormContact] = useState({ email: '', name: '', status: 'subscribed' });
  const [formCampaign, setFormCampaign] = useState({ subject: '', sent_at: '', list_id: '' as string | number });
  const [metricsCampaignId, setMetricsCampaignId] = useState<number | null>(null);
  const [metricsData, setMetricsData] = useState<any>(null);

  const loadLists = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getLists();
      setLists(Array.isArray(data) ? data : []);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al cargar listas');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadContacts = useCallback(async () => {
    if (!selectedListId) return;
    setLoading(true);
    try {
      const data = await getListContacts(selectedListId);
      setContacts(Array.isArray(data) ? data : []);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al cargar contactos');
    } finally {
      setLoading(false);
    }
  }, [selectedListId]);

  const loadCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getCampaigns(selectedListId ?? undefined);
      setCampaigns(Array.isArray(data) ? data : []);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al cargar campañas');
    } finally {
      setLoading(false);
    }
  }, [selectedListId]);

  const loadMailupStatus = useCallback(async () => {
    try {
      const s = await getMailupStatus();
      setMailupConnected(s.connected);
    } catch (_) {
      setMailupConnected(false);
    }
  }, []);

  useEffect(() => { loadLists(); loadMailupStatus(); }, [loadLists, loadMailupStatus]);
  useEffect(() => { if (subTab === 'contacts' && selectedListId) loadContacts(); }, [subTab, selectedListId, loadContacts]);
  useEffect(() => { if (subTab === 'campaigns') loadCampaigns(); }, [subTab, loadCampaigns]);
  useEffect(() => { if (metricsCampaignId) getCampaignMetrics(metricsCampaignId).then(setMetricsData); }, [metricsCampaignId]);

  const handleSaveList = async () => {
    try {
      if (editingId) {
        await updateList(editingId, {
          name: formList.name || undefined,
          description: formList.description || undefined,
          external_id: formList.external_id ? parseInt(formList.external_id, 10) : undefined,
        });
      } else {
        await createList({
          name: formList.name || undefined,
          description: formList.description || undefined,
          external_id: formList.external_id ? parseInt(formList.external_id, 10) : undefined,
        });
      }
      setModal(null);
      setEditingId(null);
      setFormList({ name: '', description: '', external_id: '' });
      loadLists();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al guardar');
    }
  };

  const handleSaveContact = async () => {
    if (!selectedListId) return;
    try {
      if (editingId) {
        await updateListContact(selectedListId, editingId, { email: formContact.email, name: formContact.name, status: formContact.status });
      } else {
        await createListContact(selectedListId, { email: formContact.email, name: formContact.name, status: formContact.status });
      }
      setModal(null);
      setEditingId(null);
      setFormContact({ email: '', name: '', status: 'subscribed' });
      loadContacts();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al guardar');
    }
  };

  const handleSaveCampaign = async () => {
    try {
      if (editingId) {
        await updateCampaign(editingId, {
          subject: formCampaign.subject || undefined,
          sent_at: formCampaign.sent_at || undefined,
          list_id: formCampaign.list_id ? parseInt(String(formCampaign.list_id), 10) : undefined,
        });
      } else {
        await createCampaign({
          subject: formCampaign.subject || undefined,
          sent_at: formCampaign.sent_at || undefined,
          list_id: formCampaign.list_id ? parseInt(String(formCampaign.list_id), 10) : undefined,
        });
      }
      setModal(null);
      setEditingId(null);
      setFormCampaign({ subject: '', sent_at: '', list_id: '' });
      loadCampaigns();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al guardar');
    }
  };

  const handleMailupSync = async (type: 'lists' | 'quick' | 'all') => {
    setMailupSyncing(true);
    setError(null);
    setSuccessMessage(null);
    try {
      if (type === 'all') {
        const result = await mailupSyncAll();
        await loadLists();
        await loadCampaigns();
        await loadMailupStatus();
        const listArray = await getLists();
        let contactsSynced = 0;
        for (const list of listArray) {
          if (list.external_id != null) {
            try {
              await mailupSyncListContacts(list.external_id);
              contactsSynced++;
            } catch (e) {
            }
          }
        }
        const count = result.lists ?? 0;
        setSuccessMessage(count > 0
          ? `Sincronización completa: ${count} lista(s), ${contactsSynced} lista(s) de contactos, métricas actualizadas.`
          : 'Sincronización correcta. No hay listas en tu cuenta Mailup; créalas en Mailup y vuelve a sincronizar.');
      } else if (type === 'quick') {
        const result = await mailupSyncAll();
        await loadLists();
        await loadCampaigns();
        await loadMailupStatus();
        const count = result.lists ?? 0;
        setSuccessMessage(count > 0
          ? `Sincronización rápida: ${count} lista(s), campañas y métricas actualizadas. (Contactos no incluidos.)`
          : 'Sincronización correcta. No hay listas en tu cuenta Mailup.');
      } else {
        const result = await mailupSyncLists();
        await loadLists();
        await loadCampaigns();
        await loadMailupStatus();
        const count = result.synced ?? result.listIds?.length ?? 0;
        setSuccessMessage(count > 0
          ? `Listas sincronizadas: ${count} lista(s).`
          : 'Sincronización correcta. No hay listas en tu cuenta Mailup.');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al sincronizar');
    } finally {
      setMailupSyncing(false);
    }
  };

  return (
    <div className="database-section">
      <div className="admin-database-header">
        <h2><ClipboardIcon width={24} height={24} /> Base de datos</h2>
        <p>Gestiona organizaciones y contactos propios o conecta Mailup para listas, campañas y métricas.</p>
      </div>

      <div className="admin-database-tabs">
        <button type="button" className={`admin-database-tab ${mainTab === 'database' ? 'active' : ''}`} onClick={() => setMainTab('database')}>Base de datos</button>
        <button type="button" className={`admin-database-tab ${mainTab === 'mailup' ? 'active' : ''}`} onClick={() => setMainTab('mailup')}>Mailup</button>
      </div>

      {mainTab === 'mailup' && (
      <div className="admin-database-subtabs">
        {(['lists', 'contacts', 'campaigns', 'metrics', 'mailup'] as SubTab[]).map((t) => (
          <button
            key={t}
            type="button"
            className={`admin-database-tab ${subTab === t ? 'active' : ''}`}
            onClick={() => setSubTab(t)}
          >
            {t === 'lists' && 'Listas'}
            {t === 'contacts' && 'Contactos'}
            {t === 'campaigns' && 'Campañas'}
            {t === 'metrics' && 'Métricas'}
            {t === 'mailup' && 'Conexión Mailup'}
          </button>
        ))}
      </div>
      )}

      {error && (
        <div className="database-toast database-toast-error">
          {error}
          <button type="button" onClick={() => setError(null)} aria-label="Cerrar"><XIcon width={16} height={16} /></button>
        </div>
      )}
      {successMessage && (
        <div className="database-toast database-toast-success">
          {successMessage}
          <button type="button" onClick={() => setSuccessMessage(null)} aria-label="Cerrar"><XIcon width={16} height={16} /></button>
        </div>
      )}

      <div className="admin-database-content">
        {mainTab === 'database' && (
          <DatabaseDataSection
            isDarkMode={isDarkMode}
            error={error}
            setError={setError}
            successMessage={successMessage}
            setSuccessMessage={setSuccessMessage}
          />
        )}
        {mainTab === 'mailup' && subTab === 'lists' && (
          <div className="admin-crm-section-card">
            <h3>Listas</h3>
            <div className="database-toolbar">
              <button type="button" className="database-btn database-btn-primary" onClick={() => { setEditingId(null); setFormList({ name: '', description: '', external_id: '' }); setModal('list'); }}>
                Nueva lista
              </button>
              <button type="button" className="database-btn database-btn-icon" onClick={loadLists}><RefreshIcon width={18} height={18} /></button>
            </div>
            {loading ? <p>Cargando...</p> : (
              <div className="admin-users-table-container">
                <table className="database-table">
                  <thead>
                    <tr><th>Id</th><th>Nombre</th><th>Descripción</th><th>External ID</th><th>Synced</th><th>Acciones</th></tr>
                  </thead>
                  <tbody>
                    {lists.map((row) => (
                      <tr key={row.id}>
                        <td>{row.id}</td>
                        <td>{row.name ?? '-'}</td>
                        <td>{row.description ?? '-'}</td>
                        <td>{row.external_id ?? '-'}</td>
                        <td>{row.synced_at ? new Date(row.synced_at).toLocaleDateString() : '-'}</td>
                        <td>
                          <button type="button" className="database-btn-link" onClick={() => { setEditingId(row.id); setFormList({ name: row.name ?? '', description: row.description ?? '', external_id: row.external_id ?? '' }); setModal('list'); }}>Editar</button>
                          <button type="button" className="database-btn-link database-btn-danger" onClick={async () => { if (window.confirm('¿Eliminar lista?')) { await deleteList(row.id); loadLists(); } }}>Eliminar</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {mainTab === 'mailup' && subTab === 'contacts' && (
          <div className="admin-crm-section-card">
            <h3>Contactos (Mailup)</h3>
            <div className="database-toolbar">
              <label>
                Lista:
                <select value={selectedListId ?? ''} onChange={(e) => setSelectedListId(e.target.value ? parseInt(e.target.value, 10) : null)}>
                  <option value="">Seleccionar lista</option>
                  {lists.map((l) => <option key={l.id} value={l.id}>{l.name || `Lista ${l.id}`}</option>)}
                </select>
              </label>
              {selectedListId && (
                <>
                  <button type="button" className="database-btn database-btn-primary" onClick={() => { setEditingId(null); setFormContact({ email: '', name: '', status: 'subscribed' }); setModal('contact'); }}>Nuevo contacto</button>
                  <button type="button" className="database-btn database-btn-icon" onClick={loadContacts}><RefreshIcon width={18} height={18} /></button>
                </>
              )}
            </div>
            {loading ? <p>Cargando...</p> : selectedListId ? (
              <div className="admin-users-table-container">
                <table className="database-table">
                  <thead>
                    <tr><th>Id</th><th>Email</th><th>Nombre</th><th>Estado</th><th>Acciones</th></tr>
                  </thead>
                  <tbody>
                    {contacts.map((row) => (
                      <tr key={row.id}>
                        <td>{row.id}</td>
                        <td>{row.email}</td>
                        <td>{row.name ?? '-'}</td>
                        <td>{row.status ?? '-'}</td>
                        <td>
                          <button type="button" className="database-btn-link" onClick={() => { setEditingId(row.id); setFormContact({ email: row.email, name: row.name ?? '', status: row.status ?? 'subscribed' }); setModal('contact'); }}>Editar</button>
                          <button type="button" className="database-btn-link database-btn-danger" onClick={async () => { if (window.confirm('¿Eliminar contacto?')) { await deleteListContact(selectedListId, row.id); loadContacts(); } }}>Eliminar</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <p>Selecciona una lista.</p>}
          </div>
        )}

        {mainTab === 'mailup' && subTab === 'campaigns' && (
          <div className="admin-crm-section-card">
            <h3>Campañas</h3>
            <div className="database-toolbar">
              <label>
                Filtrar por lista:
                <select value={selectedListId ?? ''} onChange={(e) => setSelectedListId(e.target.value ? parseInt(e.target.value, 10) : null)}>
                  <option value="">Todas</option>
                  {lists.map((l) => <option key={l.id} value={l.id}>{l.name || `Lista ${l.id}`}</option>)}
                </select>
              </label>
              <button type="button" className="database-btn database-btn-primary" onClick={() => { setEditingId(null); setFormCampaign({ subject: '', sent_at: '', list_id: '' }); setModal('campaign'); }}>Nueva campaña</button>
              <button type="button" className="database-btn database-btn-icon" onClick={loadCampaigns}><RefreshIcon width={18} height={18} /></button>
            </div>
            {loading ? <p>Cargando...</p> : (
              <div className="admin-users-table-container">
                <table className="database-table">
                  <thead>
                    <tr><th>Id</th><th>Asunto</th><th>Lista</th><th>Enviado</th><th>Acciones</th></tr>
                  </thead>
                  <tbody>
                    {campaigns.map((row) => (
                      <tr key={row.id}>
                        <td>{row.id}</td>
                        <td>{row.subject ?? '-'}</td>
                        <td>{row.list_id ?? '-'}</td>
                        <td>{row.sent_at ? new Date(row.sent_at).toLocaleString() : '-'}</td>
                        <td>
                          <button type="button" className="database-btn-link" onClick={() => { setEditingId(row.id); setFormCampaign({ subject: row.subject ?? '', sent_at: row.sent_at ? new Date(row.sent_at).toISOString().slice(0, 16) : '', list_id: row.list_id ?? '' }); setModal('campaign'); }}>Editar</button>
                          <button type="button" className="database-btn-link" onClick={() => setMetricsCampaignId(row.id)}>Métricas</button>
                          <button type="button" className="database-btn-link database-btn-danger" onClick={async () => { if (window.confirm('¿Eliminar campaña?')) { await deleteCampaign(row.id); loadCampaigns(); } }}>Eliminar</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {mainTab === 'mailup' && subTab === 'metrics' && (
          <div className="admin-crm-section-card">
            <h3>Métricas</h3>
          <div className="database-metrics">
            <p>Selecciona una campaña en la pestaña Campañas y pulsa &quot;Métricas&quot; para ver envíos, aperturas, clics y rebotes. Las métricas se actualizan al sincronizar con Mailup.</p>
            {metricsCampaignId && metricsData && (
              <div className="database-metrics-card">
                <h4>Campaña #{metricsCampaignId}</h4>
                <ul>
                  <li>Enviados: {metricsData.sent_count ?? 0}</li>
                  <li>Entregados: {metricsData.delivered_count ?? 0}</li>
                  <li>Aperturas: {metricsData.open_count ?? 0}</li>
                  <li>Clics: {metricsData.click_count ?? 0}</li>
                  <li>Rebotes: {metricsData.bounce_count ?? 0}</li>
                  <li>Bajas: {metricsData.unsubscribed_count ?? 0}</li>
                  <li>Última sync: {metricsData.synced_at ? new Date(metricsData.synced_at).toLocaleString() : '-'}</li>
                </ul>
              </div>
            )}
          </div>
          </div>
        )}

        {mainTab === 'mailup' && subTab === 'mailup' && (
          <div className="admin-crm-section-card database-mailup-card">
            <h3><SettingsIcon width={22} height={22} /> Conexión Mailup</h3>
            <p>Conecta tu cuenta de Mailup para sincronizar listas, contactos y métricas con esta base de datos.</p>
            <p><strong>Estado:</strong> {mailupConnected ? 'Conectado' : 'No conectado'}</p>
            {!mailupConnected ? (
              <button type="button" className="database-btn database-btn-primary" onClick={() => { window.location.href = getMailupOAuthUrl(); }}>
                Conectar con Mailup
              </button>
            ) : (
              <div className="database-mailup-actions">
                <button type="button" className="database-btn database-btn-primary" disabled={mailupSyncing} onClick={() => handleMailupSync('quick')}>
                  {mailupSyncing ? 'Sincronizando...' : 'Sincronización rápida'}
                </button>
                <span className="database-mailup-hint">Listas + campañas + métricas (unos segundos)</span>
                <button type="button" className="database-btn database-btn-secondary" disabled={mailupSyncing} onClick={() => handleMailupSync('lists')}>
                  Solo listas
                </button>
                <button type="button" className="database-btn database-btn-secondary" disabled={mailupSyncing} onClick={() => handleMailupSync('all')}>
                  Sincronizar todo (incl. contactos)
                </button>
                <span className="database-mailup-hint">Incluye contactos; puede tardar más</span>
              </div>
            )}
          </div>
        )}
      </div>

      {modal === 'list' && (
        <Modal isOpen onClose={() => setModal(null)} title={editingId ? 'Editar lista' : 'Nueva lista'}>
          <div className="database-form">
            <label>Nombre <input value={formList.name} onChange={(e) => setFormList((f) => ({ ...f, name: e.target.value }))} /></label>
            <label>Descripción <input value={formList.description} onChange={(e) => setFormList((f) => ({ ...f, description: e.target.value }))} /></label>
            <label>External ID (Mailup) <input type="number" value={formList.external_id} onChange={(e) => setFormList((f) => ({ ...f, external_id: e.target.value }))} placeholder="Opcional" /></label>
            <div className="database-form-actions">
              <button type="button" className="database-btn" onClick={() => setModal(null)}>Cancelar</button>
              <button type="button" className="database-btn database-btn-primary" onClick={handleSaveList}>Guardar</button>
            </div>
          </div>
        </Modal>
      )}

      {modal === 'contact' && (
        <Modal isOpen onClose={() => setModal(null)} title={editingId ? 'Editar contacto' : 'Nuevo contacto'}>
          <div className="database-form">
            <label>Email <input type="email" value={formContact.email} onChange={(e) => setFormContact((f) => ({ ...f, email: e.target.value }))} required /></label>
            <label>Nombre <input value={formContact.name} onChange={(e) => setFormContact((f) => ({ ...f, name: e.target.value }))} /></label>
            <label>Estado <select value={formContact.status} onChange={(e) => setFormContact((f) => ({ ...f, status: e.target.value }))}><option value="subscribed">Subscribed</option><option value="unsubscribed">Unsubscribed</option><option value="pending">Pending</option></select></label>
            <div className="database-form-actions">
              <button type="button" className="database-btn" onClick={() => setModal(null)}>Cancelar</button>
              <button type="button" className="database-btn database-btn-primary" onClick={handleSaveContact}>Guardar</button>
            </div>
          </div>
        </Modal>
      )}

      {modal === 'campaign' && (
        <Modal isOpen onClose={() => setModal(null)} title={editingId ? 'Editar campaña' : 'Nueva campaña'}>
          <div className="database-form">
            <label>Asunto <input value={formCampaign.subject} onChange={(e) => setFormCampaign((f) => ({ ...f, subject: e.target.value }))} /></label>
            <label>Fecha envío <input type="datetime-local" value={formCampaign.sent_at} onChange={(e) => setFormCampaign((f) => ({ ...f, sent_at: e.target.value }))} /></label>
            <label>Lista <select value={formCampaign.list_id} onChange={(e) => setFormCampaign((f) => ({ ...f, list_id: e.target.value }))}><option value="">Ninguna</option>{lists.map((l) => <option key={l.id} value={l.id}>{l.name || l.id}</option>)}</select></label>
            <div className="database-form-actions">
              <button type="button" className="database-btn" onClick={() => setModal(null)}>Cancelar</button>
              <button type="button" className="database-btn database-btn-primary" onClick={handleSaveCampaign}>Guardar</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default DatabaseSection;
